import { de, enGB } from 'date-fns/locale';
import { differenceInCalendarDays, isAfter, isFriday, isFuture, isToday, isWeekend, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

/** @global Available to all applications, originally built for allowance app  **/
export const DATE_FORMAT = 'yyyy-MM-dd';

/** @example Fri, 24 Jul 2020 */
export const CHECKIN_CHECKOUT_DATE_WITH_YEAR = 'E, d MMM y';

export const CHECKIN_CHECKOUT_DATE_WITH_YEAR_US = 'E, MMM d y';

/** @example 2020-01-17 */
export const ISO_DATE_FORMAT = 'yyyy-MM-dd';
export const TIMEZONE_EUROPE_DUBLIN = 'Europe/Dublin';
export const TIMEZONE_UTC = 'Etc/UTC';
export const dateLocales = {
  de,
  en: enGB,
};

/** @example Tue, 26 Apr*/
export const DAY_OF_WEEK = 'E, d MMM';

/** @example Tue, Apr 26 */
export const DAY_OF_WEEK_US = 'E, MMM d';

/** @example Fri, 24. Jul*/
export const DAY_OF_WEEK_DE = 'E, d. MMM';

/** @example Wednesday 18 December 2019 */
export const DAY_OF_WEEK_LONG = 'EEEE d MMMM yyyy';

export const DAY_OF_WEEK_LONG_US = 'EEEE MMMM d yyyy';

/** @example Wednesday */
export const NAME_OF_DAY_OF_WEEK = 'EEEE';

/** @example 26/12/2019 */
export const DAY_MONTH_YEAR = 'dd/MM/yyyy';

/** @example 26.12.2019 */
export const DAY_MONTH_YEAR_DE = 'dd.MM.yyyy';

/** @example 12/26/2019 */
export const DAY_MONTH_YEAR_US = 'MM/dd/yyyy';

/** @example 12/26/2019 12:00:00 AM */
export const MONTH_DAY_YEAR_WITH_TIME = 'MM/dd/yyyy hh:mm:ss a';

export const US_TIME_FORMAT = 'hh:mm a';
export const EURO_TIME_FORMAT = 'HH:mm';
export const HOUR_MINUTES_SECONDS = 'HH:mm:ss';

/** @example 10 Jun */
export const DAY_AND_MONTH_NAME = 'd MMM';

/** @example Jun 19 */
export const MONTH_NAME_AND_YEAR = 'MMM yy';

/** @example Jun 2022 */
export const MONTH_NAME_AND_FULL_YEAR = 'MMM yyyy';

/** @example June 19 */
export const FULL_MONTH_NAME_DAY_AND_YEAR = 'MMMM d, yyyy';

/** @example June 2023 */
export const FULL_MONTH_NAME_AND_YEAR = 'MMMM yyyy';

/** @example Fri, 24 Jul*/
export const CHECKIN_CHECKOUT_DATE = 'E, d MMM';

export const CHECKIN_CHECKOUT_DATE_US = 'E, MMM d';

/** @example 10 AM */
export const HOUR_12_CLOCK = 'h a';

/** @example 13:00 */
export const HOUR_MINUTE_24_CLOCK = 'HH:mm';

/** @example 24 Jun, 10 AM */
export const DAY_HOUR_12_CLOCK = 'd MMM, h a';

/** @example 01, 02, ..., 31 */
export const DAY_OF_MONTH_ZERO_FILL = 'dd';

/** @example MAY, JUNE, ... */
export const MONTH_NAME = 'MMM';

/** @example 2021-04 */
export const YEAR_MONTH = 'yyyy-MM';

/** @example 2020-01-17 10:30 */
export const FILE_NAME = 'yyyy-MM-dd HH:mm';

/** @example 2020-01-17 10:30 */
export const FILE_NAME_US = 'yyyy-dd-MM HH:mm';

/** @example Sun, 21 Mar 2021*/
export const DAY_NAME_MONTH_NAME_YEAR = 'EE, d MMM yyyy';

/** @example Sunday, 21 March*/
export const FULL_DAY_NAME_MONTH_NAME = 'EEEE, d MMMM';

/** @example Sun, Mar 21 2021*/
export const DAY_NAME_MONTH_NAME_YEAR_US = 'EE, MMM d yyyy';

/** @example Sun, 21 Mar 2021, 12:00*/
export const DAY_NAME_MONTH_NAME_YEAR_WITH_TIME = `${DAY_NAME_MONTH_NAME_YEAR}, ${HOUR_MINUTE_24_CLOCK}`;

/** @example Sunday, 21 March, 12:00*/
export const FULL_DAY_NAME_MONTH_NAME_WITH_TIME = `${FULL_DAY_NAME_MONTH_NAME}, ${HOUR_MINUTE_24_CLOCK}`;

/**
 * @returns boolean true if the date is before today or more, false otherwise
 * @param valueToTest
 */
export const isInThePast = (valueToTest: Date | string) => {
  const argStr = Object.prototype.toString.call(valueToTest);
  const compareDate =
    valueToTest instanceof Date || (typeof valueToTest === 'object' && argStr === '[object Date]')
      ? valueToTest
      : parseISO(valueToTest);
  return !(isFuture(compareDate) || isToday(compareDate));
};

export const canAddRoomexPayCard = (checkInDateString: string) => {
  const currentDate: Date = utcToZonedTime(new Date(), 'Europe/Dublin');
  const checkInDate: Date = new Date(checkInDateString);
  const daysDifference = differenceInCalendarDays(checkInDate, currentDate);
  const isCheckInToday: boolean = daysDifference === 0;
  const isTodayFriday: boolean = isFriday(currentDate);
  const isTodayWeekend: boolean = isWeekend(currentDate);
  const isBetweenMondayAndThursday: boolean = !isTodayFriday && !isTodayWeekend;

  const isAfterAGivenHour = (hour: number): boolean =>
    isAfter(currentDate, utcToZonedTime(new Date().setHours(hour, 0, 0, 0), 'Europe/Dublin'));

  if (!isCheckInToday) {
    return true;
  }

  if (isBetweenMondayAndThursday && isAfterAGivenHour(19)) {
    return false;
  }

  if (isTodayFriday && isAfterAGivenHour(18)) {
    return false;
  }

  if (isTodayWeekend && isAfterAGivenHour(16)) {
    return false;
  }

  return true;
};

export const parsedFreeCancellationDate = (GMTTime: string, hotelTime?: string | null): Date => {
  const date = hotelTime || GMTTime;

  if (date.match(/\d{4}-\d{2}-\d{2}T.+/)) {
    const [year, month, day, time] = date.split(/[T+-]/g);
    return parseISO(`${year}-${month}-${day}T${time}`);
  }
  return new Date(date);
};

export const addAbsoluteTimeZoneOffset = (date: Date): Date => {
  const userTimezoneOffset = Math.abs(date.getTimezoneOffset()) * 60000;
  return new Date(date.getTime() + userTimezoneOffset);
};

export const convertMSToTimeObject = (milliseconds) => {
  let seconds = Math.floor(milliseconds / 1000);
  let minute = Math.floor(seconds / 60);
  let hour = Math.floor(minute / 60);
  const day = Math.floor(hour / 24);
  seconds = seconds % 60;
  minute = minute % 60;
  hour = hour % 24;
  return {
    day,
    hour,
    minute,
    seconds,
  };
};

export const removeTimeZoneOffset = (date: Date): Date => {
  const userTimezoneOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - userTimezoneOffset);
};
